import { isValidCampaignSlug, useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext, useRaundLifeCycleLoadingStatus } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import { NotFound } from '@/components/Layout/NotFound'
import { useSelector } from '@xstate/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type CampaignNotFoundGuardProps = {
  children: ReactNode
}

/**
 * 存在しないキャンペーンへのアクセスをインターセプトして 404 ビューを表示するガード。
 *
 * - campaignSlug が無効（hydration 前等）のときは children をそのまま描画する
 * - ライフサイクルのロード中（status !== 'loaded'）は children を描画する
 *   → 各ページのスケルトンが表示され、404 のチラつきを防ぐ
 * - ロード完了かつ campaignNotFound のとき NotFound を表示する（URL は維持・Header/Nav は出さない）
 *   → 「ホームへ」ボタンは遷移先が同じ不存在スラグになりループするため非表示にする
 */
export function CampaignNotFoundGuard({ children }: CampaignNotFoundGuardProps) {
  const { t } = useTranslation()
  const { campaignSlug } = useCampaignSlug()
  const appActorRef = AppMachineContext.useActorRef()
  const campaignNotFound = useSelector(appActorRef, (s) => s.context.campaignNotFound)
  const { status } = useRaundLifeCycleLoadingStatus()

  if (!isValidCampaignSlug(campaignSlug)) {
    return <>{children}</>
  }

  if (status === 'loaded' && campaignNotFound) {
    return (
      <NotFound withHome={false} message={t('error.404.campaign_not_found', 'キャンペーンが見つかりませんでした。')} />
    )
  }

  return <>{children}</>
}
